'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  sendMessage,
  deleteChat,
  updateChat,
  deleteMessage,
  editMessage,
  createChat,
} from '../../lib/api/meridian-api-client.js';
import type { Chat, Message, CreateChatRequest } from '../../types/index.js';

export interface UseSendMessageMutationOptions {
  apiBaseUrl?: string;
  onSuccess?: (message: Message) => void;
  onError?: (error: Error) => void;
}

export interface UseDeleteChatMutationOptions {
  apiBaseUrl?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseUpdateChatMutationOptions {
  apiBaseUrl?: string;
  onSuccess?: (chat: Chat) => void;
  onError?: (error: Error) => void;
}

export interface UseDeleteMessageMutationOptions {
  apiBaseUrl?: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export interface UseEditMessageMutationOptions {
  apiBaseUrl?: string;
  onSuccess?: (message: Message) => void;
  onError?: (error: Error) => void;
}

export interface UseCreateChatMutationOptions {
  apiBaseUrl?: string;
  onSuccess?: (chat: Chat) => void;
  onError?: (error: Error) => void;
}

/**
 * Mutation hook for sending messages
 * Automatically invalidates chat list and messages cache after send
 */
export function useSendMessageMutation(options: UseSendMessageMutationOptions = {}) {
  const {
    apiBaseUrl = '/api/meridian',
    onSuccess,
    onError,
  } = options;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chatId, content }: { chatId: string; content: string }) => {
      const response = await sendMessage(apiBaseUrl, {
        chatId,
        content,
        role: 'user',
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to send message');
      }

      return response.data;
    },
    onSuccess: (message) => {
      // Invalidate messages cache for this chat
      queryClient.invalidateQueries({
        queryKey: ['messages', { chatId: message?.id }],
      });

      // Invalidate chats list (to update lastMessageAt, etc.)
      queryClient.invalidateQueries({
        queryKey: ['chats'],
      });

      onSuccess?.(message as Message);
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });
}

/**
 * Mutation hook for deleting chats
 * Automatically invalidates chat list cache after delete
 */
export function useDeleteChatMutation(options: UseDeleteChatMutationOptions = {}) {
  const {
    apiBaseUrl = '/api/meridian',
    onSuccess,
    onError,
  } = options;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatId: string) => {
      const response = await deleteChat(apiBaseUrl, chatId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete chat');
      }
    },
    onSuccess: () => {
      // Invalidate all chats queries (including entity-specific ones)
      // This will force a refetch of all chat lists
      queryClient.invalidateQueries({
        queryKey: ['chats'],
      });

      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });
}

/**
 * Mutation hook for updating chat metadata
 * Automatically invalidates chat list cache after update
 */
export function useUpdateChatMutation(options: UseUpdateChatMutationOptions = {}) {
  const {
    apiBaseUrl = '/api/meridian',
    onSuccess,
    onError,
  } = options;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ chatId, title, status }: { chatId: string; title?: string; status?: 'active' | 'archived' }) => {
      const response = await updateChat(apiBaseUrl, {
        chatId,
        title,
        status,
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to update chat');
      }

      return response.data;
    },
    onSuccess: (chat) => {
      // Invalidate chats list
      queryClient.invalidateQueries({
        queryKey: ['chats'],
      });

      onSuccess?.(chat as Chat);
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });
}

/**
 * Mutation hook for deleting messages
 * Automatically invalidates messages cache after delete
 */
export function useDeleteMessageMutation(options: UseDeleteMessageMutationOptions = {}) {
  const {
    apiBaseUrl = '/api/meridian',
    onSuccess,
    onError,
  } = options;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageId: string) => {
      const response = await deleteMessage(apiBaseUrl, messageId);

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete message');
      }
    },
    onSuccess: () => {
      // Invalidate all messages queries
      queryClient.invalidateQueries({
        queryKey: ['messages'],
      });

      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });
}

/**
 * Mutation hook for editing messages
 * Automatically invalidates messages cache after edit
 */
export function useEditMessageMutation(options: UseEditMessageMutationOptions = {}) {
  const {
    apiBaseUrl = '/api/meridian',
    onSuccess,
    onError,
  } = options;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageId, content }: { messageId: string; content: string }) => {
      const response = await editMessage(apiBaseUrl, messageId, content);

      if (!response.success) {
        throw new Error(response.error || 'Failed to edit message');
      }

      return response.data;
    },
    onSuccess: (message) => {
      // Invalidate all messages queries
      queryClient.invalidateQueries({
        queryKey: ['messages'],
      });

      onSuccess?.(message as Message);
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });
}

/**
 * Mutation hook for creating chats
 * Automatically invalidates chat list cache after create
 */
export function useCreateChatMutation(options: UseCreateChatMutationOptions = {}) {
  const {
    apiBaseUrl = '/api/meridian',
    onSuccess,
    onError,
  } = options;

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateChatRequest) => {
      const response = await createChat(apiBaseUrl, request);

      if (!response.success) {
        throw new Error(response.error || 'Failed to create chat');
      }

      return response.data;
    },
    onSuccess: (chat) => {
      // Invalidate chats list
      queryClient.invalidateQueries({
        queryKey: ['chats'],
      });

      onSuccess?.(chat as Chat);
    },
    onError: (error) => {
      onError?.(error as Error);
    },
  });
}
